import _ from 'lodash';
import React, { Component } from 'react';
import numeral from 'numeral';
import moment from 'moment';
import { Table, Tabs } from 'antd';
import SpinnerError from '$components/library/SpinnerError';
import { stringSortProp } from '$lib/sort';
import { Query } from '@apollo/react-components';
import { gql } from '@apollo/client';

import styles from './styles.scss';

const { TabPane } = Tabs;

const ORGANIZATION = gql`
  query OrganizationOverview($input: OrganizationInput!) {
    products {
      products {
        uuid
        name
        shortName
        slug
        pages
      }
    }
    organization(input: $input) {
      organization {
        uuid
        name
        userCourseSummaries {
          user {
            uuid
            email
          }
          quizTimeMs
          readingTimeMs
          totalTimeMs
          pagesRead
          productSlug
          lastExamScore
          purchasedAt
        }
      }
    }
  }
`;

const ExamTab = ({ product, userCourseSummaries }) => {
  const { pages } = product;

  const columns = [
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      sorter: stringSortProp('user.email'),
    },
    {
      title: 'Total time',
      dataIndex: 'totalTimeMs',
      sorter: (a, b) => a.totalTimeMs - b.totalTimeMs,
      render: (text, _record, _index) => {
        if (!text) {
          return 'none';
        }
        return moment.duration(text).humanize();
      },
    },
    {
      title: 'Quiz time',
      dataIndex: 'quizTimeMs',
      sorter: (a, b) => a.quizTimeMs - b.quizTimeMs,
      render: (text, _record, _index) => {
        if (!text) {
          return 'none';
        }
        return moment.duration(text).humanize();
      },
    },
    {
      title: 'Reading time',
      dataIndex: 'readingTimeMs',
      sorter: (a, b) => a.readingTimeMs - b.readingTimeMs,
      render: (text, _record, _index) => {
        if (!text) {
          return 'none';
        }
        return moment.duration(text).humanize();
      },
    },
    {
      title: 'Pages read',
      dataIndex: 'pagesRead',
      sorter: (a, b) => a.pagesRead - b.pagesRead,
      render: (text, _record, _index) => {
        const percent = numeral(text / pages).format('0%');
        return `${percent} (${text}/${pages})`;
      },
    },
    {
      title: 'Last exam',
      dataIndex: 'lastExamScore',
      render: (text, _record, _index) => {
        if (!text) {
          return '-';
        }
        const percent = numeral(text).format('0%');
        return percent;
      },
    },
    {
      title: 'Purchased',
      dataIndex: 'purchasedAt',
      sorter: (a, b) => a.purchasedAt - b.purchasedAt,
      render: (text, _record, _index) => moment(text).format('MM/DD/YYYY'),
      defaultSortOrder: 'descend',
    },
  ];

  const dataSource = userCourseSummaries;

  return (
    <div>
      <Table
        rowKey={(userCourseSummary) => userCourseSummary.user.uuid}
        dataSource={dataSource}
        columns={columns}
      />
    </div>
  );
};

class OrganizationOverview extends Component {
  render() {
    const { match } = this.props;
    const { params } = match;
    const { organizationUuid } = params;

    return (
      <div className={styles.layout}>
        <Query query={ORGANIZATION} variables={{ input: { organizationUuid } }}>
          {({ loading, error, data }) => {
            if (loading || error) {
              return <SpinnerError error={error} />;
            }

            const {
              organization: { organization },
              products: { products },
            } = data;
            const { name, userCourseSummaries } = organization;

            const userCourseSummariesByProductSlug = _.groupBy(
              userCourseSummaries,
              'productSlug'
            );
            const productSlugs = _.sortBy(_.keys(userCourseSummariesByProductSlug));
            const productsBySlug = _.keyBy(products, 'slug');

            return (
              <div>
                <h1>{name}</h1>
                <br />
                <h3>Data guide</h3>
                <dl>
                  <dt>Total time</dt>
                  <dd>Time engaged with all content</dd>

                  <dt>Quiz time</dt>
                  <dd>Time spent on quizzes and practice exams</dd>

                  <dt>Reading time</dt>
                  <dd>Time spent on textbook content</dd>

                  <dt>Pages</dt>
                  <dd>Self-indicated textbook pages read</dd>
                </dl>
                <div>
                  <small>
                    Time spent is an estimate. We calculate this based on user
                    interactions with the content, but due to browser privacy constraints,
                    we&apos;re limited in what we can do and prone to overestimating in
                    some situations, e.g. when a user is watching TV but also has
                    Achievable open.
                  </small>
                </div>
                <br />
                <Tabs>
                  {productSlugs.map((productSlug) => {
                    const product = productsBySlug[productSlug];
                    const userCourseSummariesForProductSlug =
                      userCourseSummariesByProductSlug[productSlug] || [];
                    return (
                      <TabPane tab={productSlug} key={productSlug}>
                        <ExamTab
                          product={product}
                          userCourseSummaries={userCourseSummariesForProductSlug}
                        />
                      </TabPane>
                    );
                  })}
                </Tabs>
              </div>
            );
          }}
        </Query>
      </div>
    );
  }
}

export default OrganizationOverview;
